import React, { useState, useEffect, useCallback } from 'react';
import { useGetApiCategoriesQuery, useGetApiTokensQuery } from '@/store/apiDocsApi.ts';
import { ApiDocEndpoint, ApiHistoryEntry } from '@/types/ApiDocs.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { cn } from '@/lib/utils.ts';
import { Clock, Key, Zap } from 'lucide-react';
import { Head } from '@/layout/Head';
import { loadHistory, saveHistory } from './utils.ts';
import { CategoryNav } from './components/CategoryNav.tsx';
import { EndpointPanel } from './components/EndpointPanel.tsx';
import { HistoryPanel } from './components/HistoryPanel.tsx';
import { TokenSelector } from './components/TokenSelector.tsx';
import { WelcomeScreen } from './components/WelcomeScreen.tsx';

// ─── הדף הראשי ───────────────────────────────────────────────────────────
export default function ApiDocsPage() {
    const { data: categories = [], isLoading: loadingCats } = useGetApiCategoriesQuery();
    const { data: tokens = [] } = useGetApiTokensQuery();

    const [selectedToken, setSelectedToken] = useState('');
    const [selectedEndpoint, setSelectedEndpoint] = useState<ApiDocEndpoint | null>(null);
    const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
    const [activeView, setActiveView] = useState<'endpoint' | 'history'>('endpoint');
    const [history, setHistory] = useState<ApiHistoryEntry[]>([]);

    // בחירת טוקן ראשון אוטומטית
    useEffect(() => {
        if (tokens.length > 0 && !selectedToken) setSelectedToken(tokens[0].token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokens]);

    // בחירת אנדפוינט ראשון אוטומטית
    useEffect(() => {
        if (categories.length > 0 && !selectedEndpoint) {
            const first = categories[0]?.endpoints?.[0];
            if (first) setSelectedEndpoint(first);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories]);

    // טעינת היסטוריה ממחסן מקומי
    useEffect(() => {
        setHistory(loadHistory());
    }, []);

    const handleHistoryAdd = useCallback((entry: ApiHistoryEntry) => {
        setHistory((prev) => {
            const updated = [...prev, entry];
            saveHistory(updated);
            return updated;
        });
    }, []);

    const handleHistoryClear = useCallback(() => {
        setHistory([]);
        saveHistory([]);
    }, []);

    const toggleCategory = (id: number) =>
        setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
        <>
        <Head title="תיעוד API" />
        <div className="flex h-[calc(100vh-64px)] font-sans font-normal" dir="rtl">
            {/* ─ Sidebar ─ */}
            <aside className="w-64 shrink-0 border-l bg-white overflow-y-auto flex flex-col">
                <div className="p-4 border-b">
                    <h1 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                        <Zap className="size-5 text-amber-500" />
                        תיעוד API
                    </h1>
                    <p className="text-xs text-gray-500 mt-1">
                        {categories.reduce((s, c) => s + c.endpoints.length, 0)} מתודות
                    </p>
                </div>

                {/* כפתור היסטוריה */}
                <button
                    onClick={() => setActiveView('history')}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2.5 text-sm border-b hover:bg-gray-50 transition-colors',
                        activeView === 'history'
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-600',
                    )}
                >
                    <Clock className="size-4" />
                    היסטוריית קריאות
                    {history.length > 0 && (
                        <Badge className="mr-auto text-[10px] py-0" variant="secondary">
                            {history.length}
                        </Badge>
                    )}
                </button>

                {/* קטגוריות */}
                <nav className="flex-1 py-2">
                    {loadingCats ? (
                        <div className="p-4 text-sm text-gray-400">טוען...</div>
                    ) : (
                        categories.map((cat) => (
                            <CategoryNav
                                key={cat.id}
                                category={cat}
                                collapsed={collapsed[cat.id] ?? false}
                                onToggle={() => toggleCategory(cat.id)}
                                selectedEndpointId={selectedEndpoint?.id}
                                onSelectEndpoint={(ep) => {
                                    setSelectedEndpoint(ep);
                                    setActiveView('endpoint');
                                }}
                            />
                        ))
                    )}
                </nav>
            </aside>

            {/* ─ Main Content ─ */}
            <main className="flex-1 overflow-y-auto flex flex-col">
                {/* סרגל טוקן */}
                <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center gap-4 flex-wrap">
                    <TokenSelector
                        tokens={tokens}
                        selected={selectedToken}
                        onSelect={setSelectedToken}
                    />
                    {!selectedToken && (
                        <span className="text-xs text-red-500 flex items-center gap-1">
                            <Key className="size-3" /> בחר טוקן לשליחת בקשות אמיתיות
                        </span>
                    )}
                </div>

                <div className="flex-1 p-6">
                    {activeView === 'history' ? (
                        <HistoryPanel history={history} onClear={handleHistoryClear} />
                    ) : selectedEndpoint ? (
                        <EndpointPanel
                            key={selectedEndpoint.id}
                            endpoint={selectedEndpoint}
                            token={selectedToken}
                            onHistoryAdd={handleHistoryAdd}
                        />
                    ) : (
                        <WelcomeScreen />
                    )}
                </div>
            </main>
        </div>
        </>
    );
}
